const DATE_PARTS_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const UNDERSCORE_PATTERN = /_/g
const INITIAL_WORD_CHARACTER_PATTERN = /^\w/

const compactNumber = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
const wholeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const currencyFormatters = new Map()

export function formatCount(value) {
  if (!Number.isFinite(value)) return '—'
  return wholeNumber.format(value)
}

export function formatCompact(value) {
  if (!Number.isFinite(value)) return '—'
  return Math.abs(value) >= 1_000_000 ? compactNumber.format(value) : wholeNumber.format(value)
}

// Short enough for a dense column: 8,800 reads as "8.8K". Pair it with
// formatCount in a title so the exact number is one hover away.
export function formatCompactShort(value) {
  if (!Number.isFinite(value)) return '—'
  return Math.abs(value) >= 1000 ? compactNumber.format(value) : wholeNumber.format(value)
}

export function formatCurrency(value, currency = 'USD', { compact = false } = {}) {
  if (!Number.isFinite(value)) return '—'
  const code = (currency || 'USD').toUpperCase()
  // The formatter depends on the magnitude as well as the options, because
  // only an abbreviated number keeps a decimal.
  const abbreviated = compact && Math.abs(value) >= 10000
  const key = `${code}:${compact}:${abbreviated}`
  if (!currencyFormatters.has(key)) {
    try {
      currencyFormatters.set(key, new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        notation: abbreviated ? 'compact' : 'standard',
        // Compact means "no small change": one decimal once it is abbreviated,
        // none at all while the number is still written out.
        maximumFractionDigits: compact ? (abbreviated ? 1 : 0) : 2,
        minimumFractionDigits: compact ? 0 : 2,
      }))
    } catch {
      currencyFormatters.set(key, null)
    }
  }
  const formatter = currencyFormatters.get(key)
  return formatter ? formatter.format(value) : `${code} ${formatCompact(value)}`
}

// Ratio in [0, 1] → "12.5%". Pass points=true for values already in percent.
export function formatPercent(value, { points = false, digits = 1 } = {}) {
  if (!Number.isFinite(value)) return '—'
  return `${(points ? value : value * 100).toFixed(digits)}%`
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${Math.round(seconds % 60)}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

// Relative change between two values. `points` compares percentages by
// difference instead of ratio. Returns null when there is nothing to compare.
// `short` is the same number without the "vs previous period" tail, for
// places too tight to carry the sentence.
export function describeChange(current, previous, { points = false } = {}) {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null
  const change = (direction, short, label) => ({ direction, short, label: label ?? `${short} vs previous period` })
  if (points) {
    const delta = current - previous
    if (Math.abs(delta) < 0.0005) return change('flat', 'No change', 'No change vs previous period')
    return change(delta > 0 ? 'up' : 'down', `${delta > 0 ? '+' : '−'}${Math.abs(delta * 100).toFixed(1)} pts`)
  }
  if (previous === 0) {
    if (current === 0) return change('flat', 'No change', 'No change vs previous period')
    // From nothing, a value is new; a negative one (a loss) is new and down.
    return change(current > 0 ? 'up' : 'down', 'New', 'New vs previous period')
  }
  const delta = ((current - previous) / Math.abs(previous)) * 100
  if (Math.abs(delta) < 0.05) return change('flat', 'No change', 'No change vs previous period')
  return change(delta > 0 ? 'up' : 'down', `${delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)}%`)
}

// A date-only key ("2026-10-01", a daily bucket) names a day, not an
// instant: parsed as UTC midnight it would read as the day before anywhere
// west of UTC, so it is read as that calendar day.
function parseDay(value) {
  const match = typeof value === 'string' ? DATE_PARTS_PATTERN.exec(value) : null
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value)
}

export function formatShortDate(value) {
  const date = value instanceof Date ? value : parseDay(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// A series bucket's label: an hourly bucket ("2026-09-20T13:00:00Z") reads
// as its local hour, a daily one as its date. The long form adds the day to
// an hour, for tooltips and tables.
export function isHourBucket(value) {
  return typeof value === 'string' && value.includes('T')
}

export function formatBucket(value, { long = false } = {}) {
  if (!isHourBucket(value)) return formatShortDate(value)
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const hour = date.toLocaleTimeString('en-US', { hour: 'numeric' })
  return long ? `${formatShortDate(date)}, ${hour}` : hour
}

export function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function humanize(value) {
  return String(value || '')
    .replace(UNDERSCORE_PATTERN, ' ')
    .replace(INITIAL_WORD_CHARACTER_PATTERN, (letter) => letter.toUpperCase())
}

// A display name, or the identifier when nothing was humanized: a code an
// operator can look up says more than an "Unknown" that hides the gap.
export function nameOrId(name, id) {
  return name || id || ''
}
